import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Background from "./../../assets/img/slider/sliderbg1.webp";
import BackgroundTwo from "./../../assets/img/slider/sliderbg2.webp";
import BackgroundThree from "./../../assets/img/slider/sliderbg3.webp";

import "./../../assets/css/slick.css";

function SliderArrow({ className = "", style = {}, onClick, direction }) {
  const isNext = direction === "next";
  const directionClass = isNext ? "slick-next" : "slick-prev";
  const iconClass = isNext ? "fa-angle-right" : "fa-angle-left";
  const ariaLabel = isNext ? "Next slide" : "Previous slide";

  return (
    <button
      type="button"
      className={`${className} ${directionClass}`.trim()}
      style={style}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      <i className={`fas ${iconClass}`} aria-hidden="true" />
    </button>
  );
}

function HeroSlide({ slide, t }) {
  return (
    <div>
      <div
        className="single-slider slider-bg"
        style={{
          backgroundImage: `url(${slide.background})`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="container">
          <div className="row">
            <div className="col-lg-7 col-md-7">
              <div className="slider-content s-slider-content mt-130">
                <h5 data-animation="fadeInUp" data-delay=".4s">
                  {t("header.Welcome")}
                </h5>

                <h2 data-animation="fadeInUp" data-delay=".4s">
                  {t("header.TakeYourPlace")}
                </h2>

                <div className="slider-btn mt-30">
                  <Link
                    to="/svi-studijski-programi"
                    className="btn ss-btn mr-15"
                    data-animation="fadeInLeft"
                    data-delay=".4s"
                  >
                    {t("header.ExplorePrograms")}{" "}
                    <i className="fas fa-arrow-right" aria-hidden="true" />
                  </Link>

                  <Link
                    to="/kontakt"
                    className="btn ss-btn active"
                    data-animation="fadeInLeft"
                    data-delay=".4s"
                  >
                    {t("header.ContactUs")}{" "}
                    <i className="fas fa-arrow-right" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>

            <div className="col-lg-5 col-md-5 p-relative" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Sliderone() {
  const { t } = useTranslation();
  const heroRef = useRef(null);
  const hasRequestedSliderRef = useRef(false);

  const [SliderComponent, setSliderComponent] = useState(null);

  const heroSlides = useMemo(
    () => [
      { id: "slide-1", background: Background },
      { id: "slide-2", background: BackgroundTwo },
      { id: "slide-3", background: BackgroundThree },
    ],
    [],
  );

  const serviceCards = useMemo(
    () => [
      {
        id: "programs",
        to: "/svi-studijski-programi",
        titleKey: "header.kartica1naslov",
        descriptionKey: "header.kartica1opis",
        iconSrc: "assets/img/icon/sve-icon4.png",
        iconAlt: "Studijski programi",
        isActive: false,
      },
      {
        id: "erasmus",
        to: "/erasmus",
        titleKey: "header.kartica2naslov",
        descriptionKey: "header.kartica2opis",
        iconSrc: "assets/img/icon/sve-icon5.png",
        iconAlt: "Erasmus",
        isActive: true,
      },
      {
        id: "accreditation",
        to: "/akreditacija-fpep",
        titleKey: "header.kartica3naslov",
        descriptionKey: "header.kartica3opis",
        iconSrc: "assets/img/icon/sve-icon6.png",
        iconAlt: "Akreditacija",
        isActive: false,
      },
    ],
    [],
  );

  useEffect(() => {
    let isCancelled = false;
    let idleCallbackId = null;
    let timeoutId = null;

    const preloadSecondarySlides = () => {
      heroSlides.slice(1).forEach((slide) => {
        const image = new Image();
        image.src = slide.background;
      });
    };

    const loadSlider = async () => {
      if (hasRequestedSliderRef.current) return;
      hasRequestedSliderRef.current = true;

      try {
        const module = await import("react-slick");

        if (isCancelled) return;

        setSliderComponent(() => module.default);
        preloadSecondarySlides();
      } catch (error) {
        console.error("Failed to load hero slider:", error);
      }
    };

    const scheduleSliderLoad = () => {
      if (typeof window !== "undefined" && "requestIdleCallback" in window) {
        idleCallbackId = window.requestIdleCallback(
          () => {
            loadSlider();
          },
          { timeout: 1800 },
        );
      } else {
        timeoutId = window.setTimeout(() => {
          loadSlider();
        }, 1200);
      }
    };

    const handleWindowLoad = () => {
      scheduleSliderLoad();
    };

    const handlePriorityInteraction = () => {
      loadSlider();
    };

    const heroNode = heroRef.current;

    if (heroNode) {
      heroNode.addEventListener("pointerenter", handlePriorityInteraction, {
        passive: true,
      });
      heroNode.addEventListener("touchstart", handlePriorityInteraction, {
        passive: true,
      });
      heroNode.addEventListener("focusin", handlePriorityInteraction, {
        passive: true,
      });
    }

    if (document.readyState === "complete") {
      scheduleSliderLoad();
    } else {
      window.addEventListener("load", handleWindowLoad, { once: true });
    }

    return () => {
      isCancelled = true;

      if (heroNode) {
        heroNode.removeEventListener("pointerenter", handlePriorityInteraction);
        heroNode.removeEventListener("touchstart", handlePriorityInteraction);
        heroNode.removeEventListener("focusin", handlePriorityInteraction);
      }

      window.removeEventListener("load", handleWindowLoad);

      if (
        idleCallbackId !== null &&
        typeof window !== "undefined" &&
        "cancelIdleCallback" in window
      ) {
        window.cancelIdleCallback(idleCallbackId);
      }

      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [heroSlides]);

  const sliderSettings = useMemo(
    () => ({
      autoplay: true,
      autoplaySpeed: 10000,
      speed: 700,
      infinite: true,
      slidesToShow: 1,
      slidesToScroll: 1,
      dots: false,
      fade: true,
      arrows: true,
      pauseOnHover: false,
      pauseOnFocus: false,
      adaptiveHeight: false,
      cssEase: "ease-in-out",
      lazyLoad: "ondemand",
      prevArrow: <SliderArrow direction="prev" />,
      nextArrow: <SliderArrow direction="next" />,
      responsive: [
        {
          breakpoint: 1200,
          settings: {
            dots: false,
            arrows: false,
          },
        },
      ],
    }),
    [],
  );

  return (
    <>
      <section id="home" className="slider-area fix p-relative" ref={heroRef}>
        {SliderComponent ? (
          <SliderComponent
            className="slider-active"
            style={{ background: "#301c28" }}
            {...sliderSettings}
          >
            {heroSlides.map((slide) => (
              <HeroSlide key={slide.id} slide={slide} t={t} />
            ))}
          </SliderComponent>
        ) : (
          <div
            className="slider-active"
            style={{ background: "#301c28" }}
            aria-label="Homepage hero"
          >
            <HeroSlide slide={heroSlides[0]} t={t} />
          </div>
        )}
      </section>

      <section className="service-details-two p-relative">
        <div className="container">
          <div className="row">
            {serviceCards.map((card) => (
              <div className="col-lg-4 col-md-12 col-sm-12" key={card.id}>
                <div
                  className={`services-box07${card.isActive ? " active" : ""}`}
                >
                  <div className="sr-contner">
                    <div className="icon">
                      <img
                        src={card.iconSrc}
                        alt={card.iconAlt}
                        loading="lazy"
                        decoding="async"
                      />
                    </div>

                    <div className="text">
                      <h5>
                        <Link to={card.to}>{t(card.titleKey)}</Link>
                      </h5>

                      <p>{t(card.descriptionKey)}</p>

                      <Link to={card.to}>
                        {t("header.SaznajVise")}{" "}
                        <i className="fas fa-arrow-right" aria-hidden="true" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default Sliderone;
